/**
 * design_handoff_accept_invitation — /accept_invitation/:email/:invitationHash.
 * State machine: checkInvitation resolves to valid / expired / not-found;
 * a valid invitation branches on Accept into either a token (existing
 * account, straight to Joined) or success: 0 (no account, Create account).
 * See InvitationScreen for why "existing vs. new address" can't be told
 * apart before that click.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import AuthLayout from "./AuthPages/AuthLayout";
import TestimonialPane from "./AuthPages/TestimonialPane";
import WhatsNewPane from "./AuthPages/WhatsNewPane";
import LoadingIndicator from "../../calendar/components/LoadingIndicator";
import { useOrganizationMembersStore } from "../../../hooks/redux/organizationMembers";
import { useUserStore } from "../../../hooks/redux/user";
import { formatError } from "../../../helpers/errors";
import InvitationScreen from "./InvitationVerificationPage/InvitationScreen";
import ConfirmDeclineScreen from "./InvitationVerificationPage/ConfirmDeclineScreen";
import DeclinedScreen from "./InvitationVerificationPage/DeclinedScreen";
import JoinedScreen from "./InvitationVerificationPage/JoinedScreen";
import CreateAccountScreen from "./InvitationVerificationPage/CreateAccountScreen";
import InvalidInvitationScreen from "./InvitationVerificationPage/InvalidInvitationScreen";

// Visible pause before the busy Joined screen hands off to the dashboard —
// matches the handoff's "auto-continues" rather than an instant redirect.
const JOINED_REDIRECT_DELAY_MS = 1200;

const InvitationVerificationPage = (props) => {
  const history = useHistory();
  // rawEmail (still percent-encoded, e.g. "+" survives as "+") is what
  // checkInvitation's GET query string needs — CheckInvitationForm
  // deliberately undoes PHP's automatic "+" -> space query decoding. The
  // decoded `email` is for POST bodies (accept/reject/register), which
  // aren't subject to that query-string quirk.
  const { email: rawEmail, invitationHash } = props.match.params;
  const email = decodeURIComponent(rawEmail);

  const [showLoading, setShowLoading] = useState(true);
  const [confirmingDecline, setConfirmingDecline] = useState(false);
  const [justJoinedNewAccount, setJustJoinedNewAccount] = useState(false);

  const {
    organizationMembers,
    checkInvitation,
    acceptInvitation,
    rejectInvitation,
  } = useOrganizationMembersStore();
  const { setUser } = useUserStore();

  const {
    checkInvitation: checkInvitationLoading,
    checkInvitationSuccess,
    checkInvitationError,
    invitationOrganizationData: organization,
    invitationRole: role,
    invitationInviterName: inviterName,
    invitationExpired: expired,

    rejectInvitation: rejectInvitationLoading,
    rejectInvitationSuccess,
    rejectInvitationError,

    acceptInvitation: acceptInvitationLoading,
    acceptInvitationSuccess,
    acceptInvitationError,
    acceptInvitationData,
  } = organizationMembers;

  useEffect(() => {
    checkInvitation({ invitationHash, email: rawEmail });
    setShowLoading(false);
  }, [rawEmail, invitationHash]);

  const isExistingAccountJoined = Boolean(
    acceptInvitationSuccess &&
      acceptInvitationData?.user &&
      acceptInvitationData?.token
  );
  const needsAccountCreation = Boolean(
    acceptInvitationSuccess && acceptInvitationData?.success === 0
  );

  useEffect(() => {
    if (!isExistingAccountJoined) {
      return;
    }
    const timer = setTimeout(() => {
      setUser(acceptInvitationData);
      history.push("/dashboard");
    }, JOINED_REDIRECT_DELAY_MS);
    return () => clearTimeout(timer);
  }, [isExistingAccountJoined, acceptInvitationData]);

  useEffect(() => {
    if (!justJoinedNewAccount) {
      return;
    }
    const timer = setTimeout(
      () => history.push("/dashboard"),
      JOINED_REDIRECT_DELAY_MS
    );
    return () => clearTimeout(timer);
  }, [justJoinedNewAccount]);

  const onAccept = useCallback(() => {
    acceptInvitation({ email, invitation_hash: invitationHash });
  }, [acceptInvitation, email, invitationHash]);

  const onDecline = useCallback(() => setConfirmingDecline(true), []);
  const onBackToInvitation = useCallback(() => setConfirmingDecline(false), []);

  const onConfirmDecline = useCallback(() => {
    rejectInvitation({ email, invitation_hash: invitationHash });
  }, [rejectInvitation, email, invitationHash]);

  const onJoinedFromCreateAccount = useCallback(
    () => setJustJoinedNewAccount(true),
    []
  );

  const onLogIn = useCallback(() => history.push("/login"), [history]);

  const acceptErrorMessage = useMemo(() => {
    if (!acceptInvitationError) {
      return null;
    }
    return formatError(
      acceptInvitationError,
      "Could not accept the invitation. Please try again."
    ).join(" ");
  }, [acceptInvitationError]);

  const rejectErrorMessage = useMemo(() => {
    if (!rejectInvitationError) {
      return null;
    }
    return formatError(
      rejectInvitationError,
      "Could not decline the invitation. Please try again."
    ).join(" ");
  }, [rejectInvitationError]);

  const isLoading = showLoading || checkInvitationLoading;

  let rightPane = <TestimonialPane />;
  let content = null;

  if (isLoading) {
    content = <LoadingIndicator />;
  } else if (checkInvitationError) {
    content = <InvalidInvitationScreen cause="generic" onLogIn={onLogIn} />;
  } else if (checkInvitationSuccess && expired) {
    content = (
      <InvalidInvitationScreen
        cause="expired"
        organization={organization}
        inviterName={inviterName}
        onLogIn={onLogIn}
      />
    );
  } else if (rejectInvitationSuccess) {
    content = (
      <DeclinedScreen
        organizationName={organization.name}
        inviterName={inviterName}
      />
    );
  } else if (isExistingAccountJoined) {
    rightPane = <WhatsNewPane />;
    content = <JoinedScreen organizationName={organization.name} />;
  } else if (justJoinedNewAccount) {
    content = <JoinedScreen organizationName={organization.name} />;
  } else if (needsAccountCreation) {
    content = (
      <CreateAccountScreen
        organization={organization}
        role={role}
        email={email}
        invitationHash={invitationHash}
        onJoined={onJoinedFromCreateAccount}
      />
    );
  } else if (confirmingDecline) {
    content = (
      <ConfirmDeclineScreen
        organization={organization}
        inviterName={inviterName}
        role={role}
        invitedEmail={email}
        loading={rejectInvitationLoading}
        errorMessage={rejectErrorMessage}
        onConfirm={onConfirmDecline}
        onBack={onBackToInvitation}
      />
    );
  } else if (checkInvitationSuccess) {
    content = (
      <InvitationScreen
        organization={organization}
        inviterName={inviterName}
        role={role}
        invitedEmail={email}
        loading={acceptInvitationLoading}
        errorMessage={acceptErrorMessage}
        onAccept={onAccept}
        onDecline={onDecline}
      />
    );
  }

  return <AuthLayout rightPane={rightPane}>{content}</AuthLayout>;
};

export default InvitationVerificationPage;
